import {useCallback, useEffect, useMemo, useState} from "react";
import {loadStripe} from "@stripe/stripe-js";
import {Elements} from "@stripe/react-stripe-js";
import StripeAddCCForm from "./StripeAddCCForm";
import {Box, HStack, Spinner, Text} from "@chakra-ui/react";
import {billingApi, paymentMethodsApi} from "../../Account/Billing/api";

// Cache the Stripe.js promise per publishable key so the bundle loads once.
// Mirrors `getStripe` in Account/Methods/MethodsView.tsx.
const stripeCache = new Map();
function getStripe(pk) {
  if (!stripeCache.has(pk)) stripeCache.set(pk, loadStripe(pk));
  return stripeCache.get(pk);
}

export default function AddCCForm({
  setErrorMessage,
  setSuccessMessage,
  onCardAdded,
  dashState,
  patient,
}) {
  const auth = useMemo(() => ({jwt: dashState?.jwt || null}), [dashState?.jwt]);
  const locationId = dashState?.selectedLocation || null;

  const [stripePublicKey, setStripePublicKey] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [preparing, setPreparing] = useState(true);

  // The publishable key and the SetupIntent are BOTH resolved server-side
  // from the same locationId, so they always belong to the same Stripe
  // account. The previous implementation read `locations[].stpPublicKey` on
  // the client while the server attached with the organization secret key;
  // under organization-scoped keys those are different accounts and the
  // attach failed with "No such PaymentMethod".
  const prepare = useCallback(async () => {
    setPreparing(true);
    setConfigError(null);
    setClientSecret(null);
    try {
      const config = await billingApi.paymentConfig(auth, locationId);
      const key = config?.stripePublicKey || null;
      if (!key) {
        throw new Error(
          "Payments are not configured for this clinic. Please contact the front desk."
        );
      }
      setStripePublicKey(key);
      const intent = await paymentMethodsApi.createSetupIntent(
        auth,
        config?.locationId ?? locationId
      );
      setClientSecret(intent?.clientSecret || null);
    } catch (e) {
      setStripePublicKey(null);
      setClientSecret(null);
      setConfigError(
        e?.message ||
          "Payments are not configured for this clinic. Please contact the front desk."
      );
    } finally {
      setPreparing(false);
    }
  }, [auth, locationId]);

  useEffect(() => {
    prepare();
  }, [prepare]);

  const appearance = useMemo(
    () => ({
      theme: "stripe",
      rules: {
        ".Tab": {
          border: "1px solid #E0E6EB",
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)",
        },
        ".Label": {color: "#A7A7A7", fontSize: "13px"},
        ".Error": {color: "#f87171", fontSize: "12px"},
        ".Tab:hover": {
          color: "var(--colorText)",
        },
      },
      variables: {
        colorPrimary: "#0570de",
        colorBackground: "#ffffff",
        colorText: "#A7A7A7",
        colorDanger: "#df1b41",
        borderRadius: "6px",
      },
    }),
    []
  );

  const stripePromise =
    stripePublicKey && clientSecret ? getStripe(stripePublicKey) : null;

  return (
    <Box textAlign={"center"} w="100%">
      {configError && (
        <Box p={4} color="red.500" fontSize="14px">
          {configError}
        </Box>
      )}

      {!configError && preparing && (
        <HStack py={4} justify="center">
          <Spinner size="sm" />
          <Text fontSize="14px" color="gray.500">
            Preparing secure form...
          </Text>
        </HStack>
      )}

      {stripePromise && clientSecret && (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance,
            locale: "en",
          }}
        >
          <StripeAddCCForm
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
            onConfirmFailed={prepare}
            onSaved={(savedPaymentMethodId) => {
              // A SetupIntent is single-use: mint a fresh one so the patient
              // can add another card without reloading the page. Remounting
              // <Elements> on the new client secret also clears the form.
              onCardAdded?.(savedPaymentMethodId);
              prepare();
            }}
            dashState={dashState}
            patient={patient}
          />
        </Elements>
      )}
    </Box>
  );
}
